import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";

const Tabs = forwardRef(function Tabs(props, ref) {
  const tabItems = props.itemNames;
  const defaultTab = props.defaultTab && tabItems.includes(props.defaultTab)
    ? props.defaultTab
    : tabItems[0];
  const [activeItem, setActiveItem] = useState(defaultTab);
  const tabViews = props.views;
  const tabBadges = props.tabBadges || {}; // Object mapping tab name to badge count
  const onTabChange = props.onTabChange;

  // Sync active tab when defaultTab (e.g. from URL) changes
  useEffect(() => {
    if (defaultTab && tabItems.includes(defaultTab)) {
      setActiveItem(defaultTab);
    }
  }, [defaultTab]);

  // Expose method to change tab programmatically
  useImperativeHandle(ref, () => ({
    changeTab: (tabName) => {
      if (tabItems.includes(tabName)) {
        setActiveItem(tabName);
        onTabChange?.(tabName);
      }
    }
  }));

  function TabPanel() {
    return (
      <Stack
        spacing={1}
        direction="row"
        sx={{
          flexWrap: "wrap",
          pb: 1,
        }}
      >
        {tabItems.map((item, i) => {
          const badgeCount = tabBadges[item];
          const hasBadge = badgeCount && badgeCount > 0;

          return (
            <Button
              key={i}
              size="medium"
              color="secondary"
              variant={getTabButtonVariant(item)}
              onClick={() => changeTabview(item)}
              sx={{
                position: 'relative',
                overflow: 'visible',
                zIndex: 1,
                borderRadius: "999px",
                px: 2,
                py: 1,
                minHeight: "34px",
                '& .MuiBadge-root': {
                  width: '100%'
                },
                '&:hover': {
                  zIndex: 2
                }
              }}
            >
              {hasBadge ? (
                <Badge
                  badgeContent={badgeCount}
                  color="error"
                  max={99}
                  sx={{
                    width: '100%',
                    "& .MuiBadge-badge": {
                      top: -6,
                      right: -6,
                      fontSize: '0.7rem',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px',
                      zIndex: 10,
                      position: 'absolute'
                    }
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="subtitle2">{item}</Typography>
                  </Box>
                </Badge>
              ) : (
                <Typography variant="subtitle2">{item}</Typography>
              )}
            </Button>
          );
        })}
      </Stack>
    );

    function getTabButtonVariant(itemName) {
      if (itemName === activeItem) return "contained";
      return "outlined";
    }

    function changeTabview(navItem) {
      setActiveItem(navItem);
      onTabChange?.(navItem);
    }
  }

  function TabView() {
    return <div>{tabViews[getActiveTabIndex()]}</div>;
  }

  function getActiveTabIndex() {
    return tabItems.indexOf(activeItem);
  }

  return (
    <div>
      <TabPanel />
      <TabView />
    </div>
  );
});

export default Tabs;
